"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import type { Editor } from "@tiptap/core";
import type { Range } from "@tiptap/core";
import { setOpenBookmarkModal } from "@/lib/bookmark-modal-helper";

interface BookmarkModalContextValue {
  openBookmarkModal: (
    editor: Editor,
    range: Range,
    position?: { top: number; left: number },
    provider?: string | null
  ) => void;
  closeBookmarkModal: () => void;
  isOpen: boolean;
  editor: Editor | null;
  range: Range | null;
  position: { top: number; left: number } | null;
  provider: string | null;
}

const BookmarkModalContext = createContext<BookmarkModalContextValue | undefined>(undefined);

export function BookmarkModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [range, setRange] = useState<Range | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [provider, setProvider] = useState<string | null>(null);

  const openBookmarkModal = useCallback(
    (
      editorInstance: Editor,
      rangeInstance: Range,
      pos?: { top: number; left: number },
      providerId?: string | null
    ) => {
      setEditor(editorInstance);
      setRange(rangeInstance);
      setPosition(pos || null);
      setProvider(providerId || null);
      setIsOpen(true);
    },
    []
  );

  const closeBookmarkModal = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setEditor(null);
      setRange(null);
      setPosition(null);
      setProvider(null);
    }, 100);
  }, []);

  useEffect(() => {
    setOpenBookmarkModal(openBookmarkModal);
    return () => {
      setOpenBookmarkModal(null);
    };
  }, [openBookmarkModal]);

  return (
    <BookmarkModalContext.Provider
      value={{
        openBookmarkModal,
        closeBookmarkModal,
        isOpen,
        editor,
        range,
        position,
        provider,
      }}
    >
      {children}
    </BookmarkModalContext.Provider>
  );
}

export function useBookmarkModalContext() {
  const context = useContext(BookmarkModalContext);
  if (context === undefined) {
    throw new Error("useBookmarkModalContext must be used within a BookmarkModalProvider");
  }
  return context;
}
